import React, { useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTourHandler } from '@/contexts/tour-handler'
import { getTicketByID } from '../../services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { TICKET_STATUS_COMPLETED, TICKET_STATUS_CLOSED } from '../../constants'
import {
    ClassificationSection,
    CustomerServiceSection,
    ContentSection,
    PriorityScheduleSection,
    AssigneesSection,
} from '../Create/sections'
import { useEditTicketForm } from './use-edit-ticket-form'
import { useCreateTicketTour } from '../Create/use-create-ticket-tour'
import { Button } from 'vnetwork-ui'

const Edit: React.FC = () => {
    const { ticketID } = useParams<{ ticketID: string }>()
    const { t } = useTranslation()
    const navigate = useNavigate()

    const {
        data: ticket,
        renderState,
        refetch: callbackTicket,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_DETAIL, ticketID],
        queryFn: async () => {
            const result = await getTicketByID({
                ticket_id: Number(ticketID) || 0,
            })
            return result
        },
    })

    const ticketData = ticket?.result

    const handleSuccess = () => {
        callbackTicket()
        navigate(`/tickets/${ticketID}`)
    }

    const handleCancel = () => {
        navigate(`/tickets/${ticketID}`)
    }

    const {
        formControl,
        control,
        handleSubmit,
        watch,
        setValue,
        isPending,
        slaInfo,
        onSubmit,
        handleServiceChange,
        handleServiceSLALoaded,
        handleChangeTicketTypeGroup,
        handleChangeTicketType,
    } = useEditTicketForm({
        ticket: ticketData,
        onSuccess: handleSuccess,
        onCancel: handleCancel,
    })

    // Use same tour as Create - no separate alert
    const { startTour } = useCreateTicketTour({ setValue, watch })
    const { registerHandler, unregisterHandler } = useTourHandler()

    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Redirect if ticket is completed or closed - not editable
    useEffect(() => {
        if (
            ticketData &&
            [TICKET_STATUS_COMPLETED, TICKET_STATUS_CLOSED].includes(
                ticketData.ticket_status
            )
        ) {
            toast.error(t('ticket-edit-not-allowed'))
            navigate(`/tickets/${ticketID}`)
        }
    }, [ticketData, navigate, ticketID, t])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // Don't render form for completed/closed tickets
    if (
        ticketData &&
        [TICKET_STATUS_COMPLETED, TICKET_STATUS_CLOSED].includes(
            ticketData.ticket_status
        )
    ) {
        return null
    }

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    { path: paths.ticket, title: t('ticket-list') },
                    {
                        path: `/tickets/${ticketID}`,
                        title: `#${ticketData?.ticket_id}`,
                    },
                    { path: '', title: t('edit') },
                ]}
            />
            <FormProvider {...formControl}>
                <form className="mt-2" data-tour="ticket-form">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
                        {/* Left Column - Main Info */}
                        <div className="lg:col-span-8 space-y-2">
                            <ClassificationSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                onChangeTicketTypeGroup={
                                    handleChangeTicketTypeGroup
                                }
                                onChangeTicketType={handleChangeTicketType}
                                disabled
                            />

                            <CustomerServiceSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                onServiceChange={handleServiceChange}
                                onServiceSLALoaded={handleServiceSLALoaded}
                            />

                            <ContentSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                ticket={ticketData}
                            />
                        </div>

                        {/* Right Column - Settings & Actions */}
                        <div className="lg:col-span-4 space-y-2">
                            <PriorityScheduleSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                slaInfo={slaInfo}
                                ticketId={ticketData?.ticket_id}
                            />

                            <AssigneesSection control={control} />

                            {/* Action Buttons */}
                            <div className="bg-white rounded-md p-4 border border-gray-100">
                                <div className="flex flex-col gap-2">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        loading={isPending}
                                        disabled={isPending}
                                        onClick={handleSubmit(onSubmit)}
                                        className="w-full"
                                    >
                                        {t('ticket-update')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        disabled={isPending}
                                        onClick={handleCancel}
                                        className="w-full"
                                    >
                                        {t('cancle-operation')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default Edit
